/**
 * Quiz Service
 * API calls for quiz functionality
 */

import apiClient from '../lib/axios';

// TW-374: a question carries nothing that names the member in the photo — options
// are numbered 0-3 and the photo is loaded by question id. The server says which
// option was right only after the answer is submitted.
export interface QuizQuestion {
  id: number;
  session_id: number;
  options: QuizOption[];
  question_number: number;
}

export interface QuizOption {
  option_id: number;
  member_name: string;
}

export interface QuizSession {
  session_id: number;
  quiz_type: 'random' | 'new' | 'all';
  total_questions: number;
  questions: QuizQuestion[];
}

export interface AnswerResponse {
  success: boolean;
  is_correct: boolean;
  correct_option: number | null;
  completed: boolean;
}

export interface QuizResults {
  session_id: number;
  total_questions: number;
  correct_answers: number;
  incorrect_answers: number;
  score_percentage: number;
  completed_at: string;
}

// Backend response types (actual API structure)
interface BackendQuizStartResponse {
  success: boolean;
  session_id: number;
  questions: Array<{
    question_id: number;
    image_url: string;
    options: Array<{
      option_id: number;
      name: string;
    }>;
  }>;
}

interface BackendAnswerResponse {
  correct: boolean;
  correct_option: number | null;
  completed: boolean;
}

interface BackendQuizResultsResponse {
  success: boolean;
  score_percent: number;
  correct: number;
  wrong: number;
  total: number;
  duration_minutes: number;
}

export const quizService = {
  /**
   * Start a new quiz session
   */
  startQuiz: async (quizType: 'random' | 'new' | 'all' = 'random'): Promise<QuizSession> => {
    const { data } = await apiClient.post<BackendQuizStartResponse>('/quiz/quiz_start', {
      quiz_type: quizType,
    });

    // Transform backend response to match frontend expectations
    const transformedQuestions: QuizQuestion[] = data.questions.map((q, index) => ({
      id: q.question_id,
      session_id: data.session_id,
      options: q.options.map(opt => ({
        option_id: opt.option_id,
        member_name: opt.name,
      })),
      question_number: index + 1,
    }));

    return {
      session_id: data.session_id,
      quiz_type: quizType,
      total_questions: transformedQuestions.length,
      questions: transformedQuestions,
    };
  },

  /**
   * Submit an answer for a question
   */
  submitAnswer: async (
    sessionId: number,
    questionId: number,
    selectedOption: number
  ): Promise<AnswerResponse> => {
    const { data } = await apiClient.post<BackendAnswerResponse>('/quiz/quiz_answer', {
      session_id: sessionId,
      question_id: questionId,
      selected_option: selectedOption,
    });

    return {
      success: true,
      is_correct: data.correct,
      correct_option: data.correct_option,
      completed: data.completed,
    };
  },

  /**
   * Fetch a question's photo as a blob URL (JWT-authenticated, served by question id).
   * The caller revokes it with URL.revokeObjectURL when the question changes.
   */
  fetchQuizPhoto: async (questionId: number): Promise<string | null> => {
    try {
      const response = await apiClient.get<Blob>('/quiz/quiz_photo', {
        responseType: 'blob',
        params: { question_id: questionId },
      });
      return URL.createObjectURL(response.data);
    } catch (error) {
      console.error(`Failed to load quiz photo for question ${questionId}:`, error);
      return null;
    }
  },

  /**
   * Get quiz results
   */
  getResults: async (sessionId: number): Promise<QuizResults> => {
    const { data } = await apiClient.get<BackendQuizResultsResponse>('/quiz/quiz_results', {
      params: { session_id: sessionId },
    });

    // Transform backend response to match frontend expectations
    return {
      session_id: sessionId,
      total_questions: data.total,
      correct_answers: data.correct,
      incorrect_answers: data.wrong,
      score_percentage: data.score_percent,
      completed_at: new Date().toISOString(), // Backend doesn't provide this, use current time
    };
  },
};
